import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import { queries } from '../../../common/api/queryKeys/queries';
import { useAbly } from '../../../common/hooks/useAbly';
import { useRole } from '../../../common/hooks/useRole';
import { OfferModel } from '../../../offer/model/OfferModel';
import { OrderModel } from '../../model/OrderModel';
import { sortOffers } from '../common/sortOffers';

export const useOrderOffers = (order?: OrderModel) => {
    const queryClient = useQueryClient();
    const { subscribe, unsubscribe } = useAbly();
    const role = useRole();

    const filterOffer = (offer: OfferModel) => {
        if (role.isSuperAdmin()) {
            return offer.wasQuoted() || !offer.isSpot();
        }

        return true;
    };

    const offerListQuery = useQuery({
        ...queries.offers.list({ orderIds: order ? [order.id] : [] }),
        enabled: !!order?.id,
        select: (data) => data.filter(filterOffer).sort(sortOffers()),
    });

    const invalidateApprovalSettings = useCallback(() => {
        offerListQuery.data?.forEach((offer) => {
            queryClient.invalidateQueries({ queryKey: queries.offers.detail(offer.id)._ctx.approvalSettings.queryKey });
        });
    }, [offerListQuery.data, queryClient]);

    useEffect(() => {
        if (!order) {
            return;
        }

        const offerTopic = `customers:${order.customerId}:orders:${order.id}:offers`;

        subscribe(offerTopic, () => {
            offerListQuery.refetch();
            invalidateApprovalSettings();
        });

        return () => {
            unsubscribe(offerTopic);
        };
    }, [offerListQuery, order, subscribe, unsubscribe, invalidateApprovalSettings]);

    useEffect(() => {
        if (role.isSupplier() || !offerListQuery.data?.length) {
            return;
        }

        const approvalRequestTopics = offerListQuery.data.map(
            (offer) => `customers:${offer.customerId}:offers:${offer.id}:approval-requests`
        );

        approvalRequestTopics.forEach((topic) => {
            subscribe(topic, () => {
                offerListQuery.refetch();
            });
        });

        return () => {
            approvalRequestTopics.forEach((topic) => {
                unsubscribe(topic);
            });
        };
    }, [offerListQuery, role, subscribe, unsubscribe]);

    return offerListQuery;
};
